
import React, { useEffect } from 'react';
import ViewToggleBar from './ViewToggleBar';
import CalendarViewContent from './CalendarViewContent';
import EventListView from './EventListView';
import { ViewMode, Event } from '@/lib/types';
import { CalendarClock, Calendar as CalendarIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useIsMobile } from '@/hooks/use-mobile';
import { sendViewChangeEvent } from '@/services/analyticsService';

interface CalendarMainContentProps {
  viewMode: ViewMode;
  currentDate: Date;
  days: Date[];
  daysToShow: number;
  cellWidth: number;
  filteredEvents: Event[];
  isFilterActive: boolean;
  toggleViewMode: () => void;
  navigatePrevious: () => void;
  navigateNext: () => void;
  onEventClick: (event: Event) => void;
  navigateToToday: () => void;
}

const CalendarMainContent: React.FC<CalendarMainContentProps> = ({
  viewMode,
  currentDate,
  days,
  daysToShow,
  cellWidth,
  filteredEvents,
  isFilterActive,
  toggleViewMode,
  navigatePrevious,
  navigateNext,
  onEventClick,
  navigateToToday
}) => {
  const isMobile = useIsMobile();

  // Analytics esemény küldése nézet váltáskor
  useEffect(() => {
    sendViewChangeEvent(viewMode);
  }, [viewMode]);

  const handleEventClick = (event: Event) => {
    if (onEventClick) {
      onEventClick(event);
    }
  };

  // Nézet váltás kezelése analytics eseménnyel
  const handleViewModeToggle = () => {
    const newViewMode = viewMode === 'calendar' ? 'list' : 'calendar';
    toggleViewMode();
    sendViewChangeEvent(newViewMode);
  };

  return (
    <div className="overflow-hidden mb-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-3 gap-3">
        <div className="flex items-center gap-3">
          {viewMode !== 'list' && <Button
            variant="default"
            size="sm"
            onClick={navigateToToday}
            className="bg-medihub-primary hover:bg-medihub-primary/90 text-white shadow-md flex items-center gap-1"
          >
            <CalendarClock className="w-4 h-4 mr-1" />
            Mai napra ugrás
          </Button>}
          {/* Mindig megjelenítjük az események számát, függetlenül a szűréstől */}
          <Badge variant="outline" className="bg-medihub-lightPurple/20 text-medihub-primary flex items-center gap-1">
            <CalendarIcon className="h-3 w-3 mr-1" />
            {filteredEvents.length} esemény{isFilterActive ? " a szűrés alapján" : ""}
          </Badge>
        </div>
      </div>

      <div className="w-full overflow-x-auto rounded-xl shadow-lg">
        {isMobile ? (
          <div className="p-4 bg-white rounded-xl shadow-md">
            <EventListView
              events={filteredEvents}
              onEventClick={handleEventClick}
            />
          </div>
        ) : (
          <>
            <ViewToggleBar
              viewMode={viewMode}
              toggleViewMode={handleViewModeToggle}
              currentDate={currentDate}
              days={days}
              showToggleButton={true}
            />
            {viewMode === 'calendar' ? (
              <CalendarViewContent
                days={days}
                daysToShow={daysToShow}
                cellWidth={cellWidth}
                currentDate={currentDate}
                filteredEvents={filteredEvents}
                navigatePrevious={navigatePrevious}
                navigateNext={navigateNext}
                onEventClick={handleEventClick}
              />
            ) : (
              <div className="p-4 bg-white">
                <EventListView
                  events={filteredEvents}
                  onEventClick={handleEventClick}
                />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default CalendarMainContent;
